import { useTranslation } from 'react-i18next';
import { GetVisitByIdStatusEnum } from 'woofwoof-api';
import Iconify from '../iconify';
import Label from '../label';
import { LabelColor } from '../label/types';

// ----------------------------------------------------------------------

/**
 * Where a visit's patient notes stand:
 *   sent  — written and delivered to the owner
 *   draft — written but kept inside the organisation, still to be finished
 *   none  — nothing written
 */
export type VisitNotesStatus = 'sent' | 'draft' | 'none';

/**
 * Statuses where a note status is worth reporting: the appointment actually
 * took place, so notes either exist or are missing. ABORTED counts — the visit
 * started and the professional ended it early, which still produces notes worth
 * sending. A visit that never happened (cancelled, rejected, or still upcoming)
 * has nothing to say about notes, so the column stays blank rather than
 * claiming "Brak" — which would read as a reproach for a note nobody owed.
 */
const STATUSES_WITH_NOTES: GetVisitByIdStatusEnum[] = [
  GetVisitByIdStatusEnum.Completed,
  GetVisitByIdStatusEnum.Aborted,
];

/**
 * Returns null when the visit's status makes a note status meaningless — the
 * caller renders nothing in that case.
 */
export const resolveVisitNotesStatus = (opts: {
  status?: GetVisitByIdStatusEnum;
  hasNotes: boolean;
  hasNotesDraft?: boolean;
}): VisitNotesStatus | null => {
  if (!opts.status || !STATUSES_WITH_NOTES.includes(opts.status)) return null;
  // A draft outranks sent notes. The two coexist when the owner already has a
  // version and the clinic is revising it without sending yet — drafting must
  // not wipe what the owner has, since notes can't be unsent. In that state the
  // visit still needs someone to finish and send, which is what this column is
  // read for and what the "Tylko z Draftem" filter selects, so reporting
  // "Dodano" there would contradict the filter.
  if (opts.hasNotesDraft) return 'draft';
  if (opts.hasNotes) return 'sent';
  return 'none';
};

const STATUS_STYLE: Record<
  VisitNotesStatus,
  { color: LabelColor; icon: string; labelKey: string }
> = {
  sent: {
    color: 'success',
    icon: 'eva:checkmark-circle-2-fill',
    labelKey: 'visit.notesStatusSent',
  },
  draft: {
    color: 'warning',
    icon: 'eva:edit-2-fill',
    labelKey: 'visit.notesStatusDraft',
  },
  none: {
    color: 'default',
    icon: 'eva:minus-circle-outline',
    labelKey: 'visit.notesStatusNone',
  },
};

export default function VisitNotesStatusBadge({
  status,
}: {
  status: VisitNotesStatus;
}) {
  const { t } = useTranslation();
  const { color, icon, labelKey } = STATUS_STYLE[status];

  return (
    <Label
      variant="soft"
      color={color}
      sx={{ textTransform: 'none', width: 'fit-content' }}
      startIcon={<Iconify icon={icon} />}
      data-testid={`visit-notes-status-${status}`}
    >
      {t(labelKey)}
    </Label>
  );
}
